import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios, { InternalAxiosRequestConfig } from 'axios';

interface Session {
  authenticated: boolean;
  can_manage: boolean;
  csrf_token: string;
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: boolean;
  accessVersion: number;
  refresh: () => Promise<Session | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({ session: null, loading: true, error: false, accessVersion: 0, refresh: async () => null, logout: async () => {} });

function applicationApiUrl(config: InternalAxiosRequestConfig): URL | null {
  const url = new URL(config.url || '', new URL(config.baseURL || window.location.origin, window.location.origin));
  return url.origin === window.location.origin && url.pathname.startsWith('/api/') ? url : null;
}

// Attach the in-memory token only to mutations on this application's API.
export function addCsrfHeader(config: InternalAxiosRequestConfig, token: string | null): InternalAxiosRequestConfig {
  if (token && applicationApiUrl(config) && !['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
    config.headers['x-rsvp-csrf'] = token;
  }
  return config;
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [accessVersion, setAccessVersion] = useState(0);
  const request = useRef(0);
  const csrf = useRef<string | null>(null);
  const pendingRefresh = useRef<Promise<Session | null> | null>(null);
  const refresh = useCallback((): Promise<Session | null> => {
    // Concurrent denials must not establish competing anonymous sessions/cookies.
    if (pendingRefresh.current) return pendingRefresh.current;
    const current = ++request.current;
    csrf.current = null;
    setLoading(true);
    setError(false);
    // Discard cached event grants even if discovery returns the same CSRF token.
    setAccessVersion(value => value + 1);
    const pending = Promise.resolve().then(async () => {
      // StrictMode may dispose this effect before its deferred discovery starts.
      if (current !== request.current) return null;
      try {
        const { data } = await axios.get<Session>('/api/auth/session');
        if (!data || typeof data.authenticated !== 'boolean' || typeof data.can_manage !== 'boolean' || typeof data.csrf_token !== 'string' || !data.csrf_token) {
          throw new Error('Invalid session response');
        }
        const next = { authenticated: data.authenticated, can_manage: data.authenticated && data.can_manage, csrf_token: data.csrf_token };
        if (current === request.current) { csrf.current = data.csrf_token; setSession(next); }
        return next;
      } catch {
        if (current === request.current) { csrf.current = null; setSession(null); setError(true); }
        return null;
      } finally {
        if (current === request.current) { pendingRefresh.current = null; setLoading(false); }
      }
    });
    pendingRefresh.current = pending;
    return pending;
  }, []);

  useEffect(() => {
    let active = true;
    const generations = new WeakMap<InternalAxiosRequestConfig, number>();
    const recoveredEditLinks = new Set<string>();
    const interceptor = axios.interceptors.request.use(config => {
      generations.set(config, request.current);
      return addCsrfHeader(config, csrf.current);
    });
    const responseInterceptor = axios.interceptors.response.use(response => {
      const url = applicationApiUrl(response.config);
      if (active && url && generations.get(response.config) === request.current) {
        recoveredEditLinks.delete(url.pathname);
        // Explicitly acquired access permits recovery from a future grant revocation.
        if (response.config.method === 'post' && (url.pathname === '/api/invitations/redeem'
          || /^\/api\/events\/[^/]+\/access$/.test(url.pathname) || url.pathname === '/api/auth/logout')) {
          recoveredEditLinks.clear();
        }
      }
      return response;
    }, async error => {
      const url = error.config && applicationApiUrl(error.config);
      if (active && url && url.pathname !== '/api/auth/session' && [401, 403].includes(error.response?.status)
        && ['EVENT_ACCESS_REQUIRED', 'CSRF_REQUIRED', 'MANAGER_REQUIRED'].includes(error.response?.data?.code)) {
        // Ignore late denials from an earlier session/effect after recovery or logout.
        if (generations.get(error.config) === request.current) {
          const editLinkDenied = error.response.data.code === 'EVENT_ACCESS_REQUIRED'
            && /^\/api\/rsvps\/edit\/[^/]+$/.test(url.pathname);
          // A permanently revoked edit capability can fail while event access stays
          // valid. Recheck the grant once, then let the form show its invalid-link
          // error instead of refreshing/remounting/fetching the same token forever.
          if (!editLinkDenied || !recoveredEditLinks.has(url.pathname)) {
            if (editLinkDenied) recoveredEditLinks.add(url.pathname);
            await refresh();
          }
        } else if (pendingRefresh.current) await pendingRefresh.current;
      }
      // Never replay the failed request, especially RSVP/create/edit/delete mutations.
      return Promise.reject(error);
    });
    refresh();
    return () => {
      active = false;
      request.current += 1; csrf.current = null; pendingRefresh.current = null;
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    await axios.post('/api/auth/logout', {});
    const next = await refresh();
    if (!next || next.authenticated) throw new Error('Logout could not be verified');
  }, [refresh]);

  return <AuthContext.Provider value={{ session, loading, error, accessVersion, refresh, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
